export const DEFAULT_CONTENT = {
  hero: {
    badge: "Privacy // Disappearance Protocol",
    titleTop: "You are",
    titleMuted: "the product.",
    titleLead: "Become the",
    titleAccent: "firewall.",
    subtext:
      "A hands-on, 12-chapter blueprint to strip away digital surveillance, disable tracking, erase yourself from data brokers and vanish from the grid. One setting at a time.",
    ctaNote: "Instant access · One-time payment",
  },
  promo: { enabled: false, code: "GHOST20", text: "for 20% off" },
  sales: {
    baseline: 1200,
    label: "operatives have ghosted the grid",
    sub: "Every purchase is one more person off the radar. Join the resistance.",
  },
  product: {
    name: "Privacy & Disappearance Pack",
    includes: [
      "12 interactive Ghost Nodes + bonus chapter",
      "Step-by-step walkthroughs (iOS, Android, Win, Mac)",
      "Tick-off action checklists for every node",
      "Data-broker opt-out scripts & templates",
      "Ghost The Grid pro tips throughout",
      "Lifetime access + all future pack updates",
    ],
  },
  whatsInside: {
    heading: "The disappearance toolkit",
    sub: "Twelve interactive Ghost Nodes. Each with plain-language walkthroughs, tick-off checklists and pro tips. No jargon, just the exact settings to change.",
    features: [
      { icon: "MapPinOff", title: "Kill Location Leaks", desc: "Disable GPS, Wi-Fi, Bluetooth & tower triangulation across every device you own." },
      { icon: "EyeOff", title: "Vanish From Google", desc: "Shut down web, app and voice activity, then auto-delete the record permanently." },
      { icon: "Fingerprint", title: "Erase Data Brokers", desc: "Opt out of Whitepages, Spokeo, BeenVerified & MyLife with step-by-step removal." },
      { icon: "ScanLine", title: "Block Fingerprinting", desc: "Neutralize supercookies, spoof your identity and browse invisible to trackers." },
      { icon: "ShieldOff", title: "Lock Down Everything", desc: "Harden social profiles, biometrics and smart-home sensors that watch your life." },
      { icon: "KeyRound", title: "Bulletproof Passwords", desc: "Close the #1 breach vector with a vaulted, rotated, monitored credential system." },
    ],
  },
  manifesto: {
    heading: "Twelve nodes. Total disappearance.",
    sub: "Follow the path node by node, stripping away exposure and locking down your digital existence, layer by layer.",
    chapters: [
      { node: "01", title: "Vanish", desc: "Kill location leaks, metadata signals & background syncing on every device." },
      { node: "02", title: "Disappear From Google", desc: "Shut down web, app & voice activity. Auto-delete everything they hoard." },
      { node: "03", title: "Erase Search Results", desc: "Remove yourself from Google results and suppress stale cached entries." },
      { node: "04", title: "Opt Out Of Data Brokers", desc: "Purge Whitepages, Spokeo, BeenVerified & MyLife, then keep them out." },
      { node: "05", title: "Control Voice History", desc: "Wipe Google, Siri & Alexa recordings and stop future logging cold." },
      { node: "06", title: "Kill Browser Fingerprints", desc: "Block fingerprinting, scrub supercookies & spoof your digital identity." },
      { node: "07", title: "Obscure Your Location Trail", desc: "Fracture GPS, Wi-Fi & Bluetooth signals. Scatter the trackers' lock." },
      { node: "08", title: "Lock Down Social Media", desc: "Private every profile, strip tagging, facial recognition & third-party access." },
      { node: "09", title: "Eliminate Biometric Tracking", desc: "Disable Face ID, voice profiles & silence every smart-home sensor." },
      { node: "10", title: "Audit App Permissions", desc: "Strip mic, camera & location access from apps bleeding your data." },
      { node: "11", title: "Master Password Defense", desc: "Vault, rotate & harden every credential. Close the #1 breach vector." },
      { node: "12", title: "Final Transmission", desc: "Lock it in permanently and stay ghosted with a 90-day defense cycle." },
    ],
    bonus: "+ Bonus Node, Encrypted Email Migration",
  },
  testimonials: [
    { handle: "GHOST_0x4F", role: "Former Data Analyst", quote: "I removed myself from 40+ data brokers in a weekend. The opt-out walkthroughs alone are worth ten times the price." },
    { handle: "NULL_ROUTE", role: "Veteran", quote: "After years of being tracked, this finally gave me a clear, no-jargon plan. My location trail is dead. My inbox is encrypted." },
    { handle: "SHADOW_MOM", role: "Concerned Parent", quote: "I'm not technical at all. Every step told me exactly which toggle to flip. Locked down my whole family's devices in an afternoon." },
    { handle: "OFFGRID_77", role: "Privacy Advocate", quote: "The manifesto structure makes it addictive, you check off nodes like a mission. Genuinely the best privacy resource I've bought." },
    { handle: "REDACTED", role: "Small Biz Owner", quote: "Googled my name before and after. The difference is night and day. Whitepages, Spokeo, MyLife, all gone." },
    { handle: "VANISH_ELLE", role: "Journalist", quote: "Browser fingerprinting was the piece I always missed. Now I move online without leaving a shadow. Essential." },
  ],
  faqs: [
    { q: "What exactly do I get?", a: "An interactive 12-chapter Notion pack with step-by-step walkthroughs, action checklists and Ghost The Grid pro tips, plus a bonus chapter on encrypted email. Delivered instantly after checkout." },
    { q: "Do I need to be technical?", a: "No. Every walkthrough is written in plain language with toggle-by-toggle instructions for iPhone, Android, Windows and Mac. If you can change a setting, you can ghost the grid." },
    { q: "Is this a one-time purchase?", a: "Yes. Pay once, own it forever, including future pack updates and new opt-out strategies as the surveillance landscape evolves." },
    { q: "How is it delivered?", a: "You get access instantly after payment. The pack lives in Notion so you can duplicate it, check off tasks and use it as your personal privacy manual." },
    { q: "Is my payment secure?", a: "Checkout is handled entirely by Stripe with bank-grade encryption. We never see or store your card details." },
  ],
  comparison: {
    heading: "One payment. Not another subscription.",
    sub: "The privacy industry wants a monthly fee forever. Ghost The Grid hands you the same power, the exact methods the services use, once, and it's yours for life.",
    ourName: "Ghost The Grid",
    ourPrice: "$29",
    ourCadence: "one-time · lifetime",
    competitors: [
      { name: "Cloaked", price: "$10", cadence: "/mo · $120+/yr" },
      { name: "DeleteMe", price: "$23", cadence: "/mo · $129+/yr" },
      { name: "Incogni", price: "$16", cadence: "/mo · $99+/yr" },
      { name: "Kanary", price: "$16", cadence: "/mo · $180+/yr" },
      { name: "Aura", price: "$12", cadence: "/mo · $144+/yr" },
    ],
    rows: [
      "One-time payment (no recurring fees)",
      "You own the methods forever",
      "Covers 30+ data brokers & big tech",
      "Works on iOS, Android, Windows & Mac",
      "No account or personal data handed over",
      "Learn the skills, not locked to a service",
    ],
  },
  addon: {
    heading: "Just want the opt-out links?",
    name: "Data Broker Opt-Out Directory",
    desc: "30+ data brokers, people-search sites & big-tech trackers, each with a direct opt-out link. Delivered instantly to your inbox. Perfect as a standalone or a fast add-on to the full pack.",
    bullets: [
      "30+ removal links, one clean list",
      "Grouped by data brokers, search & marketing",
      "Delivered instantly by email, no waiting",
      "Re-check reminders & pro tips included",
    ],
  },
  consulting: {
    heading: "Want an expert to do it with you?",
    sub: "Book a private screen-share and I'll walk you through every setting, phone, PC, or both. Send screenshots of anything you're unsure about, ask anything, go at your pace.",
    note: "Patient, judgment-free help, especially great for parents & older family members. After checkout, book your time right here in the live chat.",
    credentials: ["CompTIA Security+", "CompTIA Linux+", "Splunk Core Certified User", "ISC2 Certified in Cybersecurity (CC)", "CompTIA Cybersecurity Career Path"],
    tiers: [
      { lookup_key: "gtg_consult_quick", name: "Quick Fix", duration: "~30 min · one problem", desc: "One nagging setting or app, locked down fast.", features: ["Live 1-on-1 screen-share", "One specific issue", "Follow-up checklist"] },
      { lookup_key: "gtg_consult_full", name: "Full Session", duration: "~60 min · multiple problems", desc: "Several devices and privacy gaps handled in one sitting.", features: ["Live 1-on-1 screen-share", "Multiple issues / devices", "Personalized action plan", "Screenshot review"], featured: true },
      { lookup_key: "gtg_consult_whiteglove", name: "White-Glove Setup", duration: "Up to 2 hrs · full lockdown", desc: "I set everything up with you, step by step. Ideal for older folks.", features: ["Everything in Full Session", "Complete device lockdown", "Opt-outs done together", "Patient, unhurried pace"] },
    ],
  },
  settings: {
    tawkto: "6ad170ce2993cc34cb7f8c60/1k5121a62",
    guarantee: "30-Day Money-Back Guarantee",
  },
};

export const ICON_OPTIONS = ["MapPinOff", "EyeOff", "Fingerprint", "ScanLine", "ShieldOff", "KeyRound", "Lock", "Shield", "Ghost", "Radar", "Wifi", "Bug"];
